import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { useFeatureFlags } from '@fs/zion-flags'
import ZionForm, { useFieldArray, useZionForm, useWatch, useFormState } from '@fs/zion-form'
import { Button, Alert, FlowGrid } from '@fs/zion-ui'
import { ContentAdd } from '@fs/zion-icon'
import {
  generateSls,
  saveSlsAndUpdateState,
  useSlsStateContext,
  useSlsDispatchContext,
  useSlsHighlightsContext,
} from '@fs/zion-sls'
import { useSharedBrowserStateContext } from '../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { useR2StateContext } from '../../../providers/r2Provider/R2Context'
import { prepareEventsDefaultValues, createEmptyEvent, getFieldIdsFromFormValues } from './utils/events.utils'
import EventFormsSection from './components/EventFormsSection'
import CardFooter from '../shared/components/CardFooter'
import getEventsDispatches from './utils/saveEvents'
import { headers } from '../../../../shared/services/config'
import { errorCollector } from '../../../../shared/utils/utils'
import { eventsResolver } from './utils/validationSchema'
import { useOverlayContext } from '../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useDisplayHighlights } from '../../../hooks/useDisplayHighlights'

/**
 * EventsCardEditMode
 *
 * Edit mode UI for a person's events. Renders a ZionForm with sections for
 * primary and additional events, manages form state, and invokes onSave when
 * the form is submitted.
 *
 * Props:
 * @param {Object} props.person - The SLS person record. Expected to include a `primary` flag and other person-specific data.
 * @param {boolean} [props.reviewed=false] - Whether the events have been reviewed. Used to toggle review state when saving.
 * @param {Function} props.setIsEditing - Function to exit edit mode (typically sets editMode state to false).
 * @param {Array<Object>} props.events - Array of event objects to populate the form (primary and additional events).
 * @param {Function} [props.onSave] - Callback invoked with form data when the form is submitted. Dispatches are also provided.
 * @param {string} [props.appName] - Name of the app context for the card.
 * @param {string|number} [props.userId] - The user ID of the person editing events (used for tracking or permissions).
 * @param {boolean} [props.saveCardDataToSls] - Whether to save the card data to SLS upon submission.
 * @param {Function} [props.onDirtyChange=(isDirty) => {}] - Callback function invoked when the form dirty state changes.
 *
 * @returns {JSX.Element} The rendered edit-mode form for events.
 */
const EventsCardEditMode = ({
  person,
  reviewed = false,
  setIsEditing,
  events,
  onSave = () => {},
  userId,
  appName,
  saveCardDataToSls,
  onDirtyChange = () => {},
}) => {
  const [t] = useTranslation()
  const { types, images, currentImageArk } = useSlsStateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const r2State = useR2StateContext()
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const {
    rip_sharedImageViewer_saveChanges: saveChangesFlag,
    rip_sharedImageViewer_sls_actions_splunk_debugger: slsActionsSplunkDebugger,
  } = useFeatureFlags(['rip_sharedImageViewer_saveChanges', 'rip_sharedImageViewer_sls_actions_splunk_debugger'])
  const slsDispatch = useSlsDispatchContext()
  const { getDispatches, commitEdits, clearEdits } = useSlsHighlightsContext()
  const { topMostPanel } = useOverlayContext()
  const topMostPanelId = useMemo(() => topMostPanel(), [topMostPanel])
  const defaultValues = useMemo(
    () => prepareEventsDefaultValues(events, types, person, images),
    [events, types, person, images]
  )

  const formMethods = useZionForm({
    defaultValues,
    mode: 'all',
    resolver: eventsResolver,
    context: { person },
  })

  const { dirtyFields } = formMethods.formState

  const primaryFieldArrayMethods = useFieldArray({
    name: 'events.primary',
    control: formMethods.control,
  })
  const additionalFieldArrayMethods = useFieldArray({
    name: 'events.additional',
    control: formMethods.control,
  })

  const watchedFormValues = useWatch({
    control: formMethods.control,
  })

  const { fields: primaryFields } = primaryFieldArrayMethods
  const { fields: additionalFields, append: appendAdditional } = additionalFieldArrayMethods

  // Calculate split screen image ARK for highlight button
  const splitScreenImageArk = thumbnails?.[r2State?.referenceFocusedIndex]?.ark

  const handleCancel = useCallback(() => {
    formMethods.reset()
    onDirtyChange(false)
    setIsEditing(false)
    clearEdits()
  }, [formMethods, setIsEditing, clearEdits, onDirtyChange])

  const handleSubmit = useCallback(
    (data) => {
      setError(null)
      setSaving(true)

      // Collect all confirmedUnlinks from events in form data
      const allConfirmedUnlinks = [
        ...(data.events.primary?.flatMap((e) => e.confirmedUnlinks || []) || []),
        ...(data.events.additional?.flatMap((e) => e.confirmedUnlinks || []) || []),
      ]
      const allConfirmedAddLinks = [
        ...(data.events.primary?.flatMap((e) => e.confirmedAddLinks || []) || []),
        ...(data.events.additional?.flatMap((e) => e.confirmedAddLinks || []) || []),
      ]

      // Get highlight dispatches from the highlights context
      const highlightDispatches = getDispatches({
        mainImageArk: currentImageArk,
        splitScreenImageArk,
      })

      const eventsDispatches = getEventsDispatches({
        currentEvents: data.events,
        eventsById: defaultValues.eventsById,
        primaryEventId: defaultValues.primaryEventId,
        defaultValues: defaultValues.events,
        images,
        person,
        appName,
        slsTypes: types,
        dirtyFields: dirtyFields.events ? dirtyFields.events : undefined,
        confirmedUnlinkList: allConfirmedUnlinks,
        confirmedAddLinkList: allConfirmedAddLinks,
        highlightDispatches,
      })

      // Combine all dispatches
      const dispatches = [...eventsDispatches]

      if (saveCardDataToSls) {
        const sls = generateSls(dispatches)

        saveSlsAndUpdateState({
          sls,
          dispatches,
          saveChangesFlag,
          slsActionsSplunkDebugger,
          userId,
          dispatch: slsDispatch,
          appName,
          fsUserAgentChain: headers['FS-User-Agent-Chain'],
          successHandler: () => {
            setSaving(false)
            onSave(data, dispatches)

            // Only batch dispatches to update local state if saveChangesFlag is off
            if (!saveChangesFlag.isOn) {
              slsDispatch({
                type: 'BATCH_DISPATCHES',
                value: dispatches,
              })
            }
            commitEdits()
          },
          errorHandler: (err) => {
            setError(err)
            setSaving(false)
            errorCollector(err, null, {
              codeId: 90,
              sls,
              status: err?.response?.status,
              responseBody: err?.response?.data,
            })
          },
          saveInitiatorFilename: 'EventsCardEditMode.js',
          // Processing SLS dispatches to update local state even if the saveChangesFlag is off
        })
      }
      if (!saveCardDataToSls) {
        setSaving(false)
        onSave(data, dispatches)
        commitEdits()
      }
    },
    [
      defaultValues.events,
      defaultValues.primaryEventId,
      defaultValues.eventsById,
      images,
      person,
      saveCardDataToSls,
      saveChangesFlag,
      slsActionsSplunkDebugger,
      slsDispatch,
      appName,
      types,
      dirtyFields.events,
      currentImageArk,
      commitEdits,
      getDispatches,
      userId,
      onSave,
      splitScreenImageArk,
    ]
  )

  // Track form dirty state to inform parent component so it can trigger unsaved changes overlay
  const { isDirty } = useFormState({ control: formMethods.control })

  // Notify parent of dirty state changes
  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  // Display highlights for all fields in edit mode
  const fieldIds = useMemo(() => getFieldIdsFromFormValues(watchedFormValues), [watchedFormValues])

  useDisplayHighlights({
    elementIds: fieldIds,
    enabled: topMostPanelId !== 'highlightsPanel',
  })

  return (
    <ZionForm methods={formMethods} onSubmit={handleSubmit} id="events-card-form">
      <FlowGrid orientation="vertical" columnCount={1} gutters="xs">
        <EventFormsSection
          fields={primaryFields}
          fieldArrayMethods={primaryFieldArrayMethods}
          title={t('eventsForm.primaryEventHeader', 'Primary Event')}
          person={person}
          isPrimarySection
          appName={appName}
          userId={userId}
          saveCardDataToSls={saveCardDataToSls}
          control={formMethods.control}
          splitScreenImageArk={splitScreenImageArk}
        />

        <EventFormsSection
          fields={additionalFields}
          fieldArrayMethods={additionalFieldArrayMethods}
          title={additionalFields.length > 0 ? t('eventsForm.additionalEventHeader', 'Additional Events') : null}
          person={person}
          isPrimarySection={false}
          appName={appName}
          userId={userId}
          saveCardDataToSls={saveCardDataToSls}
          control={formMethods.control}
          splitScreenImageArk={splitScreenImageArk}
        />

        <Button
          Icon={ContentAdd}
          emphasis="medium"
          onClick={() => {
            formMethods.trigger()
            appendAdditional(createEmptyEvent(false, types))
          }}
          data-testid="add-event-button"
          linkName="events-add-event"
        >
          {t('eventsForm.additionalEvent', 'Additional Event')}
        </Button>

        {error && (
          <Alert type="error">{t('events-card.save-error-msg', 'Error saving data. Please try again later.')}</Alert>
        )}

        <CardFooter
          reviewed={reviewed}
          hasData={
            (watchedFormValues.events.additional.length > 0 &&
              watchedFormValues.events.additional.some((field) => field.eventType)) ||
            watchedFormValues.events.primary.length > 0
          }
          onCancel={handleCancel}
          saving={saving}
          label="events"
        />
      </FlowGrid>
    </ZionForm>
  )
}
EventsCardEditMode.propTypes = {
  person: PropTypes.shape({
    // Required properties
    id: PropTypes.string,
    recordId: PropTypes.string.isRequired,
    primary: PropTypes.bool,
  }),
  reviewed: PropTypes.bool,
  setIsEditing: PropTypes.func,
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    })
  ),
  onSave: PropTypes.func.isRequired,
  onDirtyChange: PropTypes.func,
  appName: PropTypes.string,
  userId: PropTypes.string.isRequired,
  saveCardDataToSls: PropTypes.bool,
}

EventsCardEditMode.defaultProps = {
  person: null,
  reviewed: false,
  setIsEditing: () => {},
  events: [],
  appName: 'FullTextApp',
}

export default EventsCardEditMode
